/**
 * Loads the iHeart Media Pixel script and sends events passed down by the server
 */

import { MiaPlaza } from "@/Reinforced.Typings";
import Log from "@/utils/Log";

export default class IHeartMediaPixel {
	private readonly options: MiaPlaza.Control.Tracking.IIHeartMediaPixelTrackingOptions;
	private loaded = false;

	constructor(options: MiaPlaza.Control.Tracking.IIHeartMediaPixelTrackingOptions) {
		this.options = options;
	}

	public load(): void {
		if (this.loaded) {
			return;
		}

		if (!this.options?.Id || this.options?.Events.length === 0) {
			return;
		}

		const iHeartMediaPixelJs = `
		(function(w, d, n){  
			if (w.MAI) return;  
			w.MAI = w.MAI || {emit: function() {(w.MAI.q = w.MAI.q || []).push(arguments);}}  
			var e = d.createElement(n); e.async=1;  
			e.src = 'https://cdn.mgln.ai/pixel.min.js';  
			var s = d.getElementsByTagName(n)[0];  
			s.parentNode.insertBefore(e, s);  
	   	})(window, document, 'script');`;
		(new Function(iHeartMediaPixelJs))();

		// The script in 'iHeartMediaPixelJs' variable creates a 'MAI' object on 'window' immediately.
		// This check ensures the object is present and the emit function is callable.
		if (typeof (window as any).MAI !== "object" || typeof (window as any).MAI?.emit !== "function") {
			void Log.error("iHeart Media Pixel: 'MAI' object or 'emit' function is not available after loading the script.");
			return;
		}

		this.loaded = true;

		(window as any).MAI.emit("conf", this.options.Id);

		for (const event of this.options.Events) {
			const data = JSON.parse(event.CustomData);
			const args = [];
			// according to https://help.magellan.ai/docs/conversion-event-pixel-scripts
			// we have to pass arguments in specific order: value, currency, orderId
			// and it also depends on event type which arguments are required/supported
			// so we only add those arguments which are defined in the CustomData JSON
			if (data) {
				if (data.value !== undefined) {
					args.push(data.value);
				}
				if (data.currency !== undefined) {
					args.push(data.currency);
				}
				if (data.orderId !== undefined) {
					args.push(data.orderId);
				}
			}

			(window as any).MAI.emit(event.EventType.toLowerCase(), ...args);
		}
	}
}
